import React, { useState, useRef } from 'react';
import {
  Button,
  Body1,
  Caption1,
  Link,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUpload24Regular,
  DocumentArrowDown24Regular,
  CheckmarkCircle24Filled,
  Warning24Filled,
} from '@fluentui/react-icons';
import { BaseDialog } from './BaseDialog';
import { apiFetch } from '../lib/apiFetch';

interface ImportCustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported?: (count: number) => void;
}

interface ImportError {
  message: string;
}

interface ImportResponse {
  imported?: number;
  customer_ids?: string[];
  booqable_sync?: string;
  warnings?: string[];
  row_count?: number;
  error?: string;
  errors?: ImportError[];
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
  },
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '8px',
    ...shorthands.padding('32px'),
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    transition: 'background-color 120ms ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  dropzoneActive: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderColor(tokens.colorBrandStroke1),
  },
  hiddenInput: {
    display: 'none',
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  templateLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
  },
  errorList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px',
    maxHeight: '240px',
    overflowY: 'auto',
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorStatusDangerBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorStatusDangerBorder1),
  },
  errorTitle: {
    color: tokens.colorStatusDangerForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  errorItem: {
    color: tokens.colorStatusDangerForeground1,
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
  },
  warningList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px',
    maxHeight: '160px',
    overflowY: 'auto',
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
  },
  warningItem: {
    color: tokens.colorNeutralForeground1,
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
  },
  successPanel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '8px',
    ...shorthands.padding('24px'),
  },
  successIcon: {
    color: tokens.colorStatusSuccessForeground1,
    width: '48px',
    height: '48px',
  },
});

type Phase = 'idle' | 'uploading' | 'errors' | 'success';

const TEMPLATE_URL = '/customers/import/template';

export default function ImportCustomersModal({
  isOpen,
  onClose,
  onImported,
}: ImportCustomersModalProps) {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [response, setResponse] = useState<ImportResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const reset = () => {
    setFile(null);
    setPhase('idle');
    setResponse(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    if (phase === 'uploading') return;
    reset();
    onClose();
  };

  const handleFile = (f: File | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setResponse({ error: 'File must be a .csv' });
      setPhase('errors');
      return;
    }
    setFile(f);
    setResponse(null);
    setPhase('idle');
  };

  const handleUpload = async () => {
    if (!file) return;
    setPhase('uploading');
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await apiFetch('/customers/import', {
        method: 'POST',
        body: form,
      });
      const body: ImportResponse = await res.json().catch(() => ({}));
      setResponse(body);
      if (res.status === 200) {
        setPhase('success');
        onImported?.(body.imported || 0);
      } else {
        setPhase('errors');
      }
    } catch (e: any) {
      setResponse({ error: e?.message || 'Upload failed' });
      setPhase('errors');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiFetch(TEMPLATE_URL);
      if (!res.ok) throw new Error('Template not available');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import-template-customers.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setResponse({ error: e?.message || 'Template download failed' });
      setPhase('errors');
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f || null);
  };

  const errors = response?.errors || [];
  const warnings = response?.warnings || [];

  const title = phase === 'success' ? 'Import complete' : 'Import customers from CSV';

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      maxWidth="640px"
      showCloseButton={phase !== 'uploading'}
      actions={
        phase === 'success' ? (
          <Button appearance="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={phase === 'uploading'}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleUpload}
              disabled={!file || phase === 'uploading'}
              icon={phase === 'uploading' ? <Spinner size="tiny" /> : undefined}
            >
              {phase === 'uploading' ? 'Importing…' : 'Import'}
            </Button>
          </>
        )
      }
    >
      <div className={styles.body}>
        {phase === 'success' ? (
          <div className={styles.successPanel}>
            <CheckmarkCircle24Filled className={styles.successIcon} />
            <Body1>
              {response?.imported || 0} customer{(response?.imported || 0) === 1 ? '' : 's'}{' '}
              imported.
            </Body1>
            <Caption1>
              Syncing to Booqable in the background — they'll appear in the Customers list now,
              and Booqable IDs will be stamped over the next couple of minutes.
            </Caption1>
            {warnings.length > 0 && (
              <div className={styles.warningList} style={{ width: '100%', marginTop: 12 }}>
                <Body1 style={{ color: tokens.colorNeutralForeground1 }}>
                  {warnings.length} warning{warnings.length === 1 ? '' : 's'}:
                </Body1>
                {warnings.map((w, i) => (
                  <div key={i} className={styles.warningItem}>
                    {w}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <Body1>
              For migrations only. CSV must match the template. Validation runs first — if any row
              fails, nothing is imported.
            </Body1>
            <Link
              as="button"
              className={styles.templateLink}
              onClick={handleDownloadTemplate}
            >
              <DocumentArrowDown24Regular />
              Download CSV template
            </Link>

            {file ? (
              <div className={styles.fileRow}>
                <span>
                  <strong>{file.name}</strong> &middot;{' '}
                  <Caption1>{(file.size / 1024).toFixed(1)} KB</Caption1>
                </span>
                <Button appearance="subtle" onClick={reset} disabled={phase === 'uploading'}>
                  Choose different file
                </Button>
              </div>
            ) : (
              <div
                className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
              >
                <ArrowUpload24Regular />
                <Body1>Drop CSV here or click to choose</Body1>
                <Caption1>UTF-8 encoded, header row required</Caption1>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className={styles.hiddenInput}
                  onChange={(e) => handleFile(e.target.files?.[0] || null)}
                />
              </div>
            )}

            {phase === 'errors' && (
              <div className={styles.errorList}>
                <div className={styles.errorTitle}>
                  <Warning24Filled style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  {response?.error || 'Import failed'}
                  {errors.length > 0 && ` — ${errors.length} row issue${errors.length === 1 ? '' : 's'}`}
                </div>
                {errors.map((e, i) => (
                  <div key={i} className={styles.errorItem}>
                    {e.message}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </BaseDialog>
  );
}
